import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getMyClientIds, getClientOptions } from '../../../lib/refonteApi';
import { useAuth } from '../../../components/AuthProvider';

export interface PortalScope {
  options: { id: string; name: string }[];
  clientId: string | null;
  setClientId: (id: string | null) => void;
  clientName: string | undefined;
  isInternal: boolean;
  isClient: boolean;
  /** Interne qui prévisualise « en tant que client » (masque tout l'interne). URL ?asclient=1 */
  viewAsClient: boolean;
  setViewAsClient: (v: boolean) => void;
  /** Rend-on comme un client ? (vrai client OU interne en preview) */
  asClient: boolean;
  resolving: boolean;
  error: string | null;
}

/**
 * Périmètre client du portail, partagé entre les pages (dashboard, registre, détail).
 * - client : ses clients rattachés (profile_clients + fallback profiles.client_id).
 * - interne (QA) : tous les clients ACTIFS cette année.
 * Le client sélectionné vit dans l'URL (?c=) → persiste d'une page à l'autre et se partage.
 */
export function usePortalScope(): PortalScope {
  const { profile } = useAuth();
  const isInternal = !!profile && profile.role !== 'client';
  const isClient = profile?.role === 'client';
  const [params, setParams] = useSearchParams();
  const urlClient = params.get('c');

  const [options, setOptions] = useState<{ id: string; name: string }[]>([]);
  const [resolving, setResolving] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      setResolving(true); setError(null);
      try {
        const opts = isInternal
          ? await getClientOptions()
          : profile ? await getClientOptions(await getMyClientIds(profile.user_id, profile.client_id)) : [];
        setOptions(opts);
        // Défaut : garde l'URL si valide, sinon 1er client.
        if (!urlClient || !opts.some(o => o.id === urlClient)) {
          const def = opts[0]?.id;
          if (def) setParams(prev => { const n = new URLSearchParams(prev); n.set('c', def); return n; }, { replace: true });
        }
      } catch (e: any) { setError(e.message); }
      setResolving(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInternal, profile]);

  const clientId = urlClient && options.some(o => o.id === urlClient) ? urlClient : (options[0]?.id ?? null);
  const setClientId = useCallback((id: string | null) => {
    setParams(prev => { const n = new URLSearchParams(prev); if (id) n.set('c', id); else n.delete('c'); return n; });
  }, [setParams]);
  const clientName = options.find(o => o.id === clientId)?.name;

  // Preview « en tant que client » (interne seulement), porté par l'URL pour persister entre pages.
  const viewAsClient = isInternal && params.get('asclient') === '1';
  const setViewAsClient = useCallback((v: boolean) => {
    setParams(prev => { const n = new URLSearchParams(prev); if (v) n.set('asclient', '1'); else n.delete('asclient'); return n; });
  }, [setParams]);
  const asClient = isClient || viewAsClient;

  return { options, clientId, setClientId, clientName, isInternal, isClient, viewAsClient, setViewAsClient, asClient, resolving, error };
}
